'use client';

import { Suspense, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { SolidNavbar } from '@/components/SolidNavbar';
import { Footer } from '@/components/Footer';
import { CatalogSidebar } from '@/components/catalog/CatalogSidebar';
import { CourseCard } from '@/components/catalog/CourseCard';
import { CatalogRightPanel } from '@/components/catalog/CatalogRightPanel';
import { CatalogBannerCarousel } from '@/components/CatalogBannerCarousel';
import { courseApi, catalogApi } from '@/lib/api';
import { CatalogEmptyState } from '@/components/catalog/CatalogEmptyState';
import type { Course, ExamType } from '@/lib/api';
import { useOwnedItems } from '@/context/OwnedItemsContext';
import { PAGE_CONTAINER } from '@/components/ui';
import { SlidersHorizontal, X, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

const PAGE_SIZE = 6;

type SortOption = 'newest' | 'price_asc' | 'price_desc' | 'discount';

function courseEffectivePrice(c: Course) {
  return c.promoPrice != null ? c.promoPrice : (c.basePrice ?? 0);
}

function CourseSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-pulse">
      <div className="h-36 bg-slate-200" />
      <div className="p-4">
        <div className="h-4 bg-slate-200 rounded w-1/3 mb-3" />
        <div className="h-5 bg-slate-200 rounded w-3/4 mb-2" />
        <div className="h-4 bg-slate-200 rounded w-full mb-4" />
        <div className="h-8 bg-slate-200 rounded" />
      </div>
    </div>
  );
}

function CourseCatalogContent() {
  const t = useTranslations('coursesCatalog');
  const searchParams = useSearchParams();
  const [courses, setCourses] = useState<Course[]>([]);
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [page, setPage] = useState(1);

  const router = useRouter();
  const pathname = usePathname();
  const { ownedCourseIds } = useOwnedItems();
  const examTypeSlug = searchParams.get('examTypeSlug') ?? undefined;
  const isFree = searchParams.get('isFree') ?? undefined;
  const hasDiscount = searchParams.get('hasDiscount') === 'true';
  const duration = searchParams.get('duration') ?? '';
  const sort = (searchParams.get('sort') ?? 'newest') as SortOption;

  useEffect(() => {
    catalogApi.getExamTypes().then(setExamTypes).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    courseApi
      .getPublishedCourses({ examTypeSlug, isFree })
      .then(setCourses)
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
    setPage(1);
  }, [examTypeSlug, isFree]);

  const activeLabel = examTypes.find(e => e.slug === examTypeSlug)?.name;
  const hasFilters = !!(examTypeSlug || isFree || hasDiscount || duration || sort !== 'newest');

  const updateParam = (key: string, value: string | null) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) params.set(key, value); else params.delete(key);
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  const sorted = [...courses]
    .filter(c => {
      if (hasDiscount && !(c.promoPrice != null && c.basePrice != null && c.promoPrice < c.basePrice)) return false;
      if (duration) {
        const total = c.totalDurationMins ?? 0;
        if (duration === 'short' && total >= 300) return false;
        if (duration === 'medium' && (total < 300 || total > 600)) return false;
        if (duration === 'long' && total <= 600) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const aOwned = ownedCourseIds.has(a.id) ? 1 : 0;
      const bOwned = ownedCourseIds.has(b.id) ? 1 : 0;
      if (aOwned !== bOwned) return aOwned - bOwned;
      switch (sort) {
        case 'price_asc': return courseEffectivePrice(a) - courseEffectivePrice(b);
        case 'price_desc': return courseEffectivePrice(b) - courseEffectivePrice(a);
        case 'discount': {
          const ad = a.promoPrice != null && a.basePrice != null ? (a.basePrice - a.promoPrice) / a.basePrice : 0;
          const bd = b.promoPrice != null && b.basePrice != null ? (b.basePrice - b.promoPrice) / b.basePrice : 0;
          return bd - ad;
        }
        default: return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <SolidNavbar breadcrumbs={[{ label: t('breadcrumb'), href: '/courses' }]} />
      <main className="bg-slate-50 pb-24">
        {/* Hero. Visible at every width so the h1 never disappears on phones. */}
        <div className="bg-pattern bg-teal-700 py-8 sm:py-10 text-white">
          <div className={PAGE_CONTAINER}>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-1">{t('title')}</h1>
            <p className="text-teal-50 text-sm">
              {loading ? t('loading') : t('tersedia', { count: sorted.length })}
              {activeLabel && <span className="font-semibold text-white"> · {activeLabel}</span>}
              {isFree === 'true' && <span className="font-semibold text-white"> · {t('gratis')}</span>}
              {isFree === 'false' && <span className="font-semibold text-white"> · {t('berbayar')}</span>}
            </p>
          </div>
        </div>

        {/* Banner — full-width, flush to edges */}
        <div className="mt-3 sm:mt-6 sm:max-w-[1280px] sm:mx-auto sm:px-6 lg:px-8">
          <CatalogBannerCarousel />
        </div>

        <div className={`${PAGE_CONTAINER} pt-4 sm:pt-0`}>
          {/* Mobile: single filter row — Filter button + exam type chips + Gratis/Berbayar */}
          <div className="flex overflow-x-auto gap-2 pb-3 -mx-4 px-4 [&::-webkit-scrollbar]:hidden lg:hidden">
            <button
              onClick={() => setShowMobileFilters(v => !v)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                showMobileFilters ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {t('filter')}
              {hasFilters && <span className="w-3.5 h-3.5 bg-teal-500 text-white rounded-full text-[9px] flex items-center justify-center">!</span>}
            </button>
            <a href="/courses" className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${!examTypeSlug && !isFree ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200'}`}>{t('semua')}</a>
            {examTypes.map(et => (
              <a key={et.slug} href={`/courses?examTypeSlug=${et.slug}`} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${examTypeSlug === et.slug ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200'}`}>{et.name}</a>
            ))}
            <a href="/courses?isFree=true" className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${isFree === 'true' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200'}`}>{t('gratis')}</a>
            <a href="/courses?isFree=false" className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${isFree === 'false' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-slate-600 border-slate-200'}`}>{t('berbayar')}</a>
            <button onClick={() => updateParam('hasDiscount', hasDiscount ? null : 'true')} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${hasDiscount ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-600 border-slate-200'}`}>{t('sedangDiskon')}</button>
          </div>

          {/* Mobile filter drawer */}
          {showMobileFilters && (
            <div className="mb-4 lg:hidden">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-slate-900">{t('filterTitle')}</span>
                <button onClick={() => setShowMobileFilters(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <Suspense>
                <CatalogSidebar examTypes={examTypes} countLabel="courses" />
              </Suspense>
            </div>
          )}

          {/* 3-column layout */}
          <div className="flex gap-6">
            {/* Left sidebar — desktop */}
            <div className="w-60 shrink-0 hidden lg:block">
              <Suspense>
                <CatalogSidebar examTypes={examTypes} countLabel="courses" />
              </Suspense>
            </div>

            {/* Main grid */}
            <div className="flex-1 min-w-0">
              {loading ? (
                <div className="grid grid-cols-2 gap-3">
                  {Array.from({ length: PAGE_SIZE }).map((_, i) => <CourseSkeleton key={i} />)}
                </div>
              ) : sorted.length === 0 ? (
                <CatalogEmptyState
                  hasFilters={hasFilters}
                  onReset={() => router.replace(pathname, { scroll: false })}
                  itemLabel="kursus"
                  allHref="/courses"
                />
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3 gap-3">
                    <p className="text-sm text-slate-500">
                      {t('menampilkan', {
                        from: (page - 1) * PAGE_SIZE + 1,
                        to: Math.min(page * PAGE_SIZE, sorted.length),
                        total: sorted.length,
                      })}
                    </p>
                    <div className="relative flex-shrink-0">
                      <select
                        value={sort}
                        onChange={e => updateParam('sort', e.target.value === 'newest' ? null : e.target.value)}
                        className="appearance-none pl-3 pr-7 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:border-teal-300 transition-colors cursor-pointer focus:outline-none focus:border-teal-400"
                      >
                        <option value="newest">{t('sortTerbaru')}</option>
                        <option value="price_asc">{t('sortHargaAsc')}</option>
                        <option value="price_desc">{t('sortHargaDesc')}</option>
                        <option value="discount">{t('sortDiskon')}</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {paginated.map(c => <CourseCard key={c.id} course={c} />)}
                  </div>
                  {totalPages > 1 && (
                    <Pagination page={page} totalPages={totalPages} onChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
                  )}
                </>
              )}
            </div>

            {/* Right panel — desktop */}
            <div className="w-64 shrink-0 hidden xl:block pl-2 border-l border-slate-200">
              <CatalogRightPanel type="courses" />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

/**
 * Server-rendered shell for the catalog — see the equivalent in app/tests/page.tsx.
 *
 * CourseCatalogContent calls useSearchParams(), so it cannot server-render. With a bare
 * <Suspense> the whole route would emit a BAILOUT_TO_CLIENT_SIDE_RENDERING stub with no h1 and
 * no links, exactly as /tests did. That is currently masked because this page short-circuits to
 * the "Segera Hadir" waitlist view before the catalog renders — it would surface the day
 * courses launch. A fallback IS server-rendered, so the static chrome reaches crawlers either
 * way.
 */
function CatalogShell() {
  const t = useTranslations('coursesCatalog');
  return (
    <>
      <SolidNavbar breadcrumbs={[{ label: t('breadcrumb'), href: '/courses' }]} />
      <main className="bg-slate-50 pb-24">
        <div className="bg-pattern bg-teal-700 py-8 sm:py-10 text-white">
          <div className={PAGE_CONTAINER}>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-1">{t('title')}</h1>
            <p className="text-teal-50 text-sm">{t('loading')}</p>
          </div>
        </div>
        <div className={`${PAGE_CONTAINER} py-8`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }, (_, i) => <CourseSkeleton key={i} />)}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default function CoursesPage() {
  return (
    <Suspense fallback={<CatalogShell />}>
      <CourseCatalogContent />
    </Suspense>
  );
}

function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  const t = useTranslations('coursesCatalog');
  const pages = buildPageRange(page, totalPages);
  return (
    <div className="flex items-center justify-center gap-1 mt-8">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="hidden sm:inline">{t('sebelumnya')}</span>
      </button>

      <div className="flex items-center gap-1">
        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`ellipsis-${i}`} className="w-9 h-9 flex items-center justify-center text-slate-400 text-sm">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p as number)}
              className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                p === page
                  ? 'bg-teal-600 text-white border border-teal-600'
                  : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {p}
            </button>
          )
        )}
      </div>

      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <span className="hidden sm:inline">{t('berikutnya')}</span>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function buildPageRange(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
  if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
}
